import React from 'react';
import { Text } from '../text';
import {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from '../../../types/component.type';
import {
  tagSizeClasses,
  tagTextSizeClasses,
  tagHighlightClasses,
  tagHoverClasses,
} from './tag.styles';
import { resolveVariantClasses } from '../../../styles/variant-classes';

export interface TagProps {
  content: string | React.ReactNode;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  className?: string;
  textClassName?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: () => void;
  isHighlighted?: boolean;
  disabled?: boolean;
}

const appearanceClassMap: Record<
  NonNullable<ComponentAppearance>,
  (variant: NonNullable<ComponentVariant>) => string
> = {
  filled: (variant) => resolveVariantClasses(variant, 'filled'),
  outline: (variant) => resolveVariantClasses(variant, 'outline'),
  soft: (variant) => resolveVariantClasses(variant, 'soft'),
  ghost: (variant) => resolveVariantClasses(variant, 'ghost'),
};

const Tag: React.FC<TagProps> = ({
  content,
  size = 'md',
  variant = 'primary',
  appearance = 'filled',
  className,
  textClassName,
  icon,
  iconPosition = 'left',
  onClick,
  isHighlighted = false,
  disabled = false,
}) => {
  const isInteractive = !disabled && !!onClick;

  const baseClasses = [
    'flex w-fit items-center justify-center gap-1 rounded-full font-medium',
    'transition-colors duration-200',
    tagSizeClasses[size],
    isInteractive ? 'cursor-pointer' : '',
    disabled ? 'opacity-60 pointer-events-none' : '',
  ];

  const appearanceClasses = [] as string[];
  if (isHighlighted) {
    const highlightAppearance = appearance || 'default';
    const highlightClass =
      tagHighlightClasses[highlightAppearance]?.[variant] || '';
    appearanceClasses.push(highlightClass);
  } else if (appearance) {
    const resolver = appearanceClassMap[appearance];
    const baseAppearanceClass = resolver ? resolver(variant) : '';
    appearanceClasses.push(baseAppearanceClass);
  }

  const interactiveClasses = [];
  if (isInteractive) {
    const hoverClass = tagHoverClasses[appearance]?.[variant] || '';
    interactiveClasses.push(hoverClass);
  }

  const textClasses = [
    'flex h-fit items-center m-0 whitespace-nowrap font-medium',
    tagTextSizeClasses[size],
    textClassName || '',
  ];

  const containerClasses = [
    ...baseClasses,
    ...appearanceClasses,
    ...interactiveClasses,
    className || '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const contentClasses = textClasses.filter(Boolean).join(' ').trim();

  return (
    <div className={containerClasses} onClick={disabled ? undefined : onClick}>
      {iconPosition === 'left' && icon}
      {typeof content === 'string' ? (
        <Text className={contentClasses}>{content}</Text>
      ) : (
        <div className={contentClasses}>{content}</div>
      )}
      {iconPosition === 'right' && icon}
    </div>
  );
};

export default Tag;
